import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { Icon } from '@developer/components';
import { joiResolver } from '@hookform/resolvers/joi';
import { TextField, Textarea, Button } from '@marketplace/ui-elements';
import { documentationFormValidationSchema } from './documentation-form.validation';
import classes from './documentation-form.module.scss';
import {useDocumentationForm} from './documentation-form.hook';

export const DocumentationForm = ({onChangeIsApplicationInEditMode}: any) => {
  const { formatMessage } = useIntl();
  const {
    applicationId,
    initialData,
    loaded,
    transformFormDataToAPI,
    isNewDocumentation,
    addDocumentations,
    updateDocumentations,
  } = useDocumentationForm();

  useEffect(() => {
    if (loaded) reset(initialData);
  }, [initialData]);

  const {
    handleSubmit,
    getValues,
    control,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm<any>({
    resolver: joiResolver(documentationFormValidationSchema({ formatMessage })),
    mode: 'onChange',
  });

  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    onChangeIsApplicationInEditMode(onChangeIsApplicationInEditMode?.DOCUMENTATION_TAB, isEditable);
  }, [isEditable]);

  const handleSaveDetails = () => {
    const formValues = getValues();

    isNewDocumentation
      ? addDocumentations(applicationId, transformFormDataToAPI(formValues))
      : updateDocumentations(applicationId, transformFormDataToAPI(formValues));
    setIsEditable(false);
  };

  const handleCancelEdit = () => {
    reset(initialData);
    setIsEditable(false);
  };

  return (
    <div>
      {!initialData ? null : (
        <div>
          <form onSubmit={handleSubmit(handleSaveDetails)}>
            <div className={classes.mainContainer}>
              <div className={classes.header}>
                <div className={classes.mainTitle}>
                  {formatMessage({
                    id: 'documentationForm_mainTitle',
                  })}
                </div>
                <Icon
                  variant={'edit-pencil'}
                  onClick={() => setIsEditable(true)}
                  isPointer
                  color={isEditable ? '#000000' : '#6e758b'}
                />
              </div>
              <div className={classes.formContainer}>
                <div className="dev-portal__dashboard__app-page__documentation__form-field-wrap__padding-bottom">
                  {formatMessage({
                    id: 'documentationForm_documentationTutorialTitle',
                  })}
                </div>
                <div className={classes.croppedFieldContainer}>
                  <TextField
                    autoComplete="off"
                    name="name"
                    control={control}
                    maxLength={100}
                    fieldId="name"
                    error={errors.name?.message}
                    disabled={!isEditable}
                    rootClassName={
                      classes.formFieldWrap && classes.formFieldCropped
                    }
                    inputClassName={
                      classes.formFieldWrap && classes.formFieldCropped
                    }
                    label={formatMessage({
                      id: 'documentationForm_documentationName',
                    })}
                  />
                  <TextField
                    name="url"
                    autoComplete="off"
                    control={control}
                    fieldId="url"
                    maxLength={2000}
                    error={errors.url?.message}
                    disabled={!isEditable}
                    rootClassName={
                      classes.formFieldWrap && classes.formFieldCropped
                    }
                    inputClassName={
                      classes.formFieldWrap && classes.formFieldCropped
                    }
                    label={formatMessage({
                      id: 'documentationForm_documentationURL',
                    })}
                  />
                </div>

                <Textarea
                  name="support_description"
                  control={control}
                  maxLength={1000}
                  fieldId="support_description"
                  label={formatMessage({
                    id: 'documentationForm_supportDescription',
                  })}
                  disabled={!isEditable}
                  error={errors.support_description?.message}
                  textareaClassName={classes.formTextarea}
                  rootClassName={classes.formTextarea}
                />
              </div>

              <div className="dev-portal__dashboard__app-page__documentation__main-title">
                {formatMessage({
                  id: 'documentationForm_licenseURLTitle',
                })}
              </div>
              <TextField
                name="licence_url"
                control={control}
                fieldId="licence_url"
                error={errors.licence_url?.message}
                disabled={!isEditable}
                maxLength={2000}
                rootClassName={
                  classes.formFieldWrap && classes.formFieldCropped
                }
                inputClassName="dev-portal__dashboard__app-page__documentation__form-field-cropped"
                label={formatMessage({
                  id: 'documentationForm_licenseURL',
                })}
              />
              {isEditable && (
                <div className={classes.formBottomWrapper}>
                  <Button
                    variant={'secondary'}
                    disabled={!isEditable}
                    label={formatMessage({
                      id: 'documentationForm_cancelButton',
                    })}
                    onClick={handleCancelEdit}
                    className="p-button p-button-secondary"
                    data-testid="cancel-button"
                  />
                  <Button
                    variant={'primary'}
                    disabled={(isValid && !isDirty) || !isValid}
                    onClick={handleSaveDetails}
                    className="p-button"
                    label={formatMessage({
                      id: 'documentationForm_saveButton',
                    })}
                  />
                </div>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
